import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { getData } from "../../../../../../actions/apiCall"
import { Col, Image, notification, Progress, Row, Select, Table, Tag, Typography, Button, Card, Space, Collapse, Spin, Alert, Flex, Divider, Badge, Tabs, Empty } from "antd"
import { AudioOutlined, FileTextOutlined, DownloadOutlined, FileSearchOutlined, ReloadOutlined, FilePdfOutlined, TrophyOutlined, CheckCircleOutlined, CloseCircleOutlined, BarChartOutlined, UserOutlined, CodeOutlined, MessageOutlined, BulbOutlined, WarningOutlined, UnorderedListOutlined } from "@ant-design/icons"
import CodingTabs from "./CodingTabs"
import MCQTabs from "./MCQPapers"
import TestLogsTab from "./TestLogsTab"
import { putData } from "../../../../../../actions/developer"
import { encryptData, decryptData } from "../../../../../../actions/crypto"
const { Title, Text } = Typography
const { Panel } = Collapse

interface ReportProps {
    id: string | null
}

const Report: React.FC<ReportProps> = ({ id }) => {
    const [reportData, setReportData] = useState(null)
    const [dataSource, setDataSource] = useState([])
    const [comprehensiveReport, setComprehensiveReport] = useState(null)
    const [isGeneratingReport, setIsGeneratingReport] = useState(false)
    const [pdfUrl, setPdfUrl] = useState<string | null>(null)
    const dispatch = useDispatch()

    useEffect(() => {
        if (!id) {
            setReportData(null)
            setDataSource([])
            return
        }
        // Reset state when id changes to prevent showing old data
        setReportData(null)
        setDataSource([])
        getReportData(id)
    }, [id])

    const onRelod = () => {
        dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true,
        })
    }

    const onChange = async (value) => {
        try {
            // Update local state immediately for better UX
            setReportData(prev => prev ? { ...prev, testResult: value } : null);

            const resData = await dispatch(putData(`/test-details/${id}`, { testResult: value }))
            // putData returns response.data.data on success (no code property) or response.data on error (with code)
            // Check if resData exists and is not an error object (error objects have code !== 200)
            if (resData && (!resData.code || resData.code === 200)) {
                notification.success({
                    message: "Success",
                    description: "Report status updated successfully.",
                })
                // Refresh data to ensure consistency
                getReportData(id);
                onRelod()
            } else {
                // Revert on error
                getReportData(id);
                notification.error({
                    message: "Error",
                    description: resData?.message || "Failed to update report status.",
                })
            }
        }
        catch (error) {
            // Revert on error
            getReportData(id);
            console.error("Error updating report status:", error)
            notification.error({
                message: "Error",
                description: "Failed to update report status.",
            })
        }
    }

    const getReportData = async (id) => {
        try {
            const resData = await dispatch(getData(`/test-setup/report/${id}`))
            if (resData?.code === 200) {
                setReportData(resData?.data)
                getColumnData(resData?.data?.report)
                // Set PDF URL if it exists
                if (resData?.data?.comprehensiveReportPdfUrl) {
                    setPdfUrl(resData.data.comprehensiveReportPdfUrl)
                }
                // Load existing comprehensive report from database if available
                if (resData?.data?.comprehensiveReport) {
                    setComprehensiveReport(resData.data.comprehensiveReport)
                }
            }
        } catch (error) {
            console.error("Error fetching report data:", error)
        }
    }

    const generateComprehensiveReport = async () => {
        if (!id) return;

        setIsGeneratingReport(true);
        try {
            const encryptedId = encryptData(id);
            const resData = await dispatch(getData(`/accel-test/ai-interview/report/${encryptedId}`));

            if (resData?.code === 200) {
                const decryptedData = decryptData(resData.data);
                if (decryptedData?.success && decryptedData?.report) {
                    setComprehensiveReport(decryptedData.report);
                    if (decryptedData.pdfUrl) {
                        setPdfUrl(decryptedData.pdfUrl);
                    }
                    // Refresh report data to get updated comprehensive report from DB
                    await getReportData(id);
                    notification.success({
                        message: decryptedData?.cached ? "Report Loaded" : "Success",
                        description: decryptedData?.cached
                            ? "Comprehensive report loaded from database."
                            : "Comprehensive report and PDF generated successfully.",
                    });
                } else {
                    throw new Error("Invalid report data received");
                }
            } else {
                throw new Error(resData?.message || "Failed to generate report");
            }
        } catch (error: any) {
            console.error("Error generating comprehensive report:", error);
            notification.error({
                message: "Error",
                description: error?.message || "Failed to generate comprehensive report. Please ensure the test is completed.",
            });
        } finally {
            setIsGeneratingReport(false);
        }
    }

    const getColumnData = (report: any) => {
        const transformed = Object.entries(report).map(([language, stats]: any, index) => {
            const correctPercent = parseFloat(stats.correctPercentage);
            const wrongPercent = parseFloat(stats.wrongPercentage);
            const leftPercent = parseFloat(stats.leftPercentage);

            let status = 'Failed';
            let color = 'red';

            if (correctPercent >= 70) {
                status = 'Passed';
                color = 'green';
            } else if (correctPercent >= 50) {
                status = 'Average';
                color = 'orange';
            }

            return {
                key: index,
                language,
                ...stats,
                correctPercent,
                wrongPercent,
                leftPercent,
                status,
                color,
            };
        });

        setDataSource(transformed);
    }

    const columns = [
        { title: 'Language', dataIndex: 'language', key: 'language' },
        { title: 'Correct', dataIndex: 'correct', key: 'correct' },
        { title: 'Wrong', dataIndex: 'wrong', key: 'wrong' },
        { title: 'Left', dataIndex: 'left', key: 'left' },
        { title: 'Total', dataIndex: 'total', key: 'total' },
        {
            title: 'Correct %',
            dataIndex: 'correctPercent',
            key: 'correctPercent',
            render: (value: number) => (
                <Progress percent={value} strokeColor={value >= 70 ? 'green' : value >= 50 ? 'orange' : 'red'} />
            ),
        },
        {
            title: 'Wrong %',
            dataIndex: 'wrongPercent',
            key: 'wrongPercent',
            render: (value: number) => (
                <Progress percent={value} strokeColor="red" />
            ),
        },
        {
            title: 'Left %',
            dataIndex: 'leftPercent',
            key: 'leftPercent',
            render: (value: number) => (
                <Progress percent={value} strokeColor="blue" />
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_: any, record: any) => <Tag color={record.color}>{record.status}</Tag>,
        },
    ];

    const isTestCompleted = reportData?.testStatus === 'completed';

    const getVerdictColor = (verdict: string) => {
        if (verdict === 'Strong Match') return '#52c41a';
        if (verdict === 'Proceed with Caution') return '#faad14';
        return '#ff4d4f';
    };

    return reportData && (
        <div className="report-container" style={{ background: '#f8f9fa', minHeight: '100vh', padding: '32px' }}>
            {/* Professional Header */}
            <Card
                style={{
                    marginBottom: '32px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: 'none',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                }}
                bodyStyle={{ padding: '24px 32px' }}
            >
                <Flex justify="space-between" align="center">
                    <div>
                        <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#1a1a1a', fontSize: '28px', letterSpacing: '-0.5px' }}>
                            Report
                        </Title>
                        <Text type="secondary" style={{ fontSize: '15px', color: '#8c8c8c', fontWeight: 500, marginTop: '4px', display: 'block' }}>
                            Overview
                        </Text>
                    </div>
                    <Space>
                        {isTestCompleted && (
                            <>
                                {!comprehensiveReport && (
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<FileSearchOutlined />}
                                        onClick={generateComprehensiveReport}
                                        loading={isGeneratingReport}
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        Generate Comprehensive Report
                                    </Button>
                                )}
                                {pdfUrl && (
                                    <Button
                                        type="default"
                                        size="large"
                                        icon={<FilePdfOutlined />}
                                        onClick={() => {
                                            const fileUrl = process.env.FILE_URL || process.env.API_URL || '';
                                            const fullPdfUrl = pdfUrl.startsWith('http') ? pdfUrl : `${fileUrl}${pdfUrl}`;
                                            window.open(fullPdfUrl, '_blank');
                                        }}
                                    >
                                        Download PDF Report
                                    </Button>
                                )}
                            </>
                        )}
                        {!isTestCompleted && (
                            <Alert
                                message="Test Not Completed"
                                description="Comprehensive report can only be generated for completed tests."
                                type="warning"
                                showIcon
                                style={{ marginRight: '16px' }}
                            />
                        )}
                        <Select
                            key={`${id}-${reportData?.testResult || 'empty'}`}
                            style={{ width: 200 }}
                            placeholder="Select Status"
                            value={reportData?.testResult ? reportData.testResult : undefined}
                            onChange={(value) => {
                                onChange(value)
                            }}
                        >
                            <Select.Option value="In-Review">In-Review</Select.Option>
                            <Select.Option value="Hold">Hold</Select.Option>
                            <Select.Option value="Rejected">Rejected</Select.Option>
                            <Select.Option value="Shortlisted">Shortlisted</Select.Option>
                        </Select>
                    </Space>
                </Flex>
            </Card>

            {/* Comprehensive Report Section */}
            {comprehensiveReport && (
                <div style={{ marginBottom: '40px' }}>
                    {/* Main Report Card */}
                    <Card
                        style={{
                            marginBottom: '32px',
                            borderRadius: '16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            border: '1px solid #e8e8e8',
                            overflow: 'hidden'
                        }}
                        headStyle={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            padding: '24px 32px'
                        }}
                        bodyStyle={{ padding: '32px' }}
                        title={
                            <Flex justify="space-between" align="center">
                                <Flex align="center" gap={16}>
                                    <div style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '16px',
                                        background: 'rgba(255,255,255,0.25)',
                                        backdropFilter: 'blur(10px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '28px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}>
                                        <TrophyOutlined />
                                    </div>
                                    <div>
                                        <Title level={3} style={{ margin: 0, color: 'white', fontWeight: 700, fontSize: '24px' }}>
                                            Comprehensive AI Interview Report
                                        </Title>
                                        <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                                            Detailed candidate assessment and evaluation
                                        </Text>
                                    </div>
                                </Flex>
                                {pdfUrl && (
                                    <Button
                                        type="default"
                                        size="large"
                                        icon={<FilePdfOutlined />}
                                        onClick={() => {
                                            const fileUrl = process.env.FILE_URL || process.env.API_URL || '';
                                            const fullPdfUrl = pdfUrl.startsWith('http') ? pdfUrl : `${fileUrl}${pdfUrl}`;
                                            window.open(fullPdfUrl, '_blank');
                                        }}
                                        style={{
                                            background: 'white',
                                            color: '#667eea',
                                            fontWeight: 600,
                                            height: '44px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                    >
                                        View/Download PDF
                                    </Button>
                                )}
                            </Flex>
                        }
                    >
                        {/* Final Verdict */}
                        {comprehensiveReport.finalVerdict && (
                            <Card
                                style={{
                                    marginBottom: '32px',
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${getVerdictColor(comprehensiveReport.finalVerdict.verdict)}08 0%, ${getVerdictColor(comprehensiveReport.finalVerdict.verdict)}03 100%)`,
                                    border: `2px solid ${getVerdictColor(comprehensiveReport.finalVerdict.verdict)}30`,
                                    boxShadow: `0 4px 16px ${getVerdictColor(comprehensiveReport.finalVerdict.verdict)}20`
                                }}
                                bodyStyle={{ padding: '28px' }}
                            >
                                <Flex align="center" gap={12} style={{ marginBottom: '20px' }}>
                                    <TrophyOutlined style={{ fontSize: '24px', color: getVerdictColor(comprehensiveReport.finalVerdict.verdict) }} />
                                    <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#1a1a1a' }}>
                                        Final Verdict
                                    </Title>
                                </Flex>
                                <Space direction="vertical" style={{ width: '100%' }} size="large">
                                    <Flex align="center" gap={16} wrap="wrap">
                                        <Flex align="center" gap={8}>
                                            <Text strong style={{ fontSize: '16px', color: '#595959', fontWeight: 600 }}>Verdict:</Text>
                                            <Tag
                                                color={comprehensiveReport.finalVerdict.verdict === 'Strong Match' ? 'green' :
                                                    comprehensiveReport.finalVerdict.verdict === 'Proceed with Caution' ? 'orange' : 'red'}
                                                style={{
                                                    fontSize: '15px',
                                                    padding: '8px 20px',
                                                    borderRadius: '24px',
                                                    fontWeight: 600,
                                                    border: 'none',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                {comprehensiveReport.finalVerdict.verdict}
                                            </Tag>
                                        </Flex>
                                        <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                                        <Flex align="baseline" gap={8}>
                                            <Text strong style={{ fontSize: '16px', color: '#595959', fontWeight: 600 }}>Overall Score:</Text>
                                            <Text style={{
                                                fontSize: '48px',
                                                fontWeight: 700,
                                                color: getVerdictColor(comprehensiveReport.finalVerdict.verdict),
                                                lineHeight: 1,
                                                letterSpacing: '-1px'
                                            }}>
                                                {comprehensiveReport.finalVerdict.overallScore}%
                                            </Text>
                                        </Flex>
                                    </Flex>
                                    <Divider style={{ margin: '16px 0', borderColor: '#e8e8e8' }} />
                                    <div style={{
                                        background: '#ffffff',
                                        padding: '20px',
                                        borderRadius: '12px',
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <Text style={{ fontSize: '15px', lineHeight: '1.85', color: '#434343', display: 'block' }}>
                                            {comprehensiveReport.finalVerdict.summary}
                                        </Text>
                                    </div>
                                </Space>
                            </Card>
                        )}

                        <Flex gap={20} style={{ marginBottom: '32px' }}>
                            {/* Key Strengths */}
                            {comprehensiveReport.keyStrengths && comprehensiveReport.keyStrengths.length > 0 && (
                                <Card
                                    style={{
                                        flex: 1,
                                        borderRadius: '16px',
                                        border: '2px solid #52c41a40',
                                        background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                                        boxShadow: '0 4px 16px rgba(82, 196, 26, 0.12)'
                                    }}
                                    headStyle={{
                                        background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                                        borderBottom: '2px solid #52c41a30',
                                        borderRadius: '16px 16px 0 0',
                                        padding: '20px 24px'
                                    }}
                                    bodyStyle={{ padding: '24px' }}
                                    title={
                                        <Flex align="center" gap={12}>
                                            <div style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '10px',
                                                background: '#52c41a',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '20px',
                                                boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                            }}>
                                                <CheckCircleOutlined />
                                            </div>
                                            <Title level={5} style={{ margin: 0, color: '#52c41a', fontWeight: 700 }}>Key Strengths</Title>
                                        </Flex>
                                    }
                                >
                                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                                        {comprehensiveReport.keyStrengths.map((strength: string, index: number) => (
                                            <div key={index} style={{
                                                background: '#ffffff',
                                                padding: '16px',
                                                borderRadius: '10px',
                                                border: '1px solid #d9f7be',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '12px'
                                            }}>
                                                <CheckCircleOutlined style={{ color: '#52c41a', marginTop: '2px', fontSize: '18px', flexShrink: 0 }} />
                                                <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#434343', flex: 1 }}>{strength}</Text>
                                            </div>
                                        ))}
                                    </Space>
                                </Card>
                            )}

                            {/* Potential Risks */}
                            {comprehensiveReport.potentialRisks && comprehensiveReport.potentialRisks.length > 0 && (
                                <Card
                                    style={{
                                        flex: 1,
                                        borderRadius: '16px',
                                        border: '2px solid #ff4d4f40',
                                        background: 'linear-gradient(135deg, #fff1f0 0%, #ffffff 100%)',
                                        boxShadow: '0 4px 16px rgba(255, 77, 79, 0.12)'
                                    }}
                                    headStyle={{
                                        background: 'linear-gradient(135deg, #fff1f0 0%, #ffffff 100%)',
                                        borderBottom: '2px solid #ff4d4f30',
                                        borderRadius: '16px 16px 0 0',
                                        padding: '20px 24px'
                                    }}
                                    bodyStyle={{ padding: '24px' }}
                                    title={
                                        <Flex align="center" gap={12}>
                                            <div style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '10px',
                                                background: '#ff4d4f',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '20px',
                                                boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)'
                                            }}>
                                                <WarningOutlined />
                                            </div>
                                            <Title level={5} style={{ margin: 0, color: '#ff4d4f', fontWeight: 700 }}>Potential Risks</Title>
                                        </Flex>
                                    }
                                >
                                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                                        {comprehensiveReport.potentialRisks.map((risk: string, index: number) => (
                                            <div key={index} style={{
                                                background: '#ffffff',
                                                padding: '16px',
                                                borderRadius: '10px',
                                                border: '1px solid #ffccc7',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '12px'
                                            }}>
                                                <CloseCircleOutlined style={{ color: '#ff4d4f', marginTop: '2px', fontSize: '18px', flexShrink: 0 }} />
                                                <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#ff4d4f', flex: 1 }}>{risk}</Text>
                                            </div>
                                        ))}
                                    </Space>
                                </Card>
                            )}
                        </Flex>

                        {/* Unified Competency Table */}
                        {comprehensiveReport.unifiedCompetencyTable && comprehensiveReport.unifiedCompetencyTable.length > 0 && (
                            <Card
                                style={{
                                    marginBottom: '32px',
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                    border: '1px solid #e8e8e8'
                                }}
                                headStyle={{
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                    borderBottom: '2px solid #e8e8e8',
                                    borderRadius: '16px 16px 0 0',
                                    padding: '20px 24px'
                                }}
                                bodyStyle={{ padding: '24px' }}
                                title={
                                    <Flex align="center" gap={12}>
                                        <BarChartOutlined style={{ fontSize: '20px', color: '#667eea' }} />
                                        <Title level={5} style={{ margin: 0, fontWeight: 700, color: '#1a1a1a' }}>
                                            Unified Competency Table
                                        </Title>
                                    </Flex>
                                }
                            >
                                <Table
                                    dataSource={comprehensiveReport.unifiedCompetencyTable.map((item: any, index: number) => ({
                                        key: index,
                                        ...item
                                    }))}
                                    columns={[
                                        {
                                            title: 'Competency',
                                            dataIndex: 'competency',
                                            key: 'competency',
                                            render: (text: string) => <Text strong style={{ color: '#1a1a1a' }}>{text}</Text>
                                        },
                                        {
                                            title: 'Score',
                                            dataIndex: 'score',
                                            key: 'score',
                                            render: (score: string) => {
                                                const numScore = parseFloat(score);
                                                const color = numScore >= 7 ? '#52c41a' : numScore >= 5 ? '#faad14' : '#ff4d4f';
                                                return <Tag color={color} style={{ fontWeight: 600, padding: '4px 12px' }}>{score}</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Synthesis',
                                            dataIndex: 'synthesis',
                                            key: 'synthesis',
                                            render: (text: string) => <Text style={{ color: '#595959', lineHeight: '1.6' }}>{text}</Text>
                                        },
                                        {
                                            title: 'Sources',
                                            dataIndex: 'sources',
                                            key: 'sources',
                                            render: (sources: string[]) => (
                                                <Space size="small">
                                                    {sources?.map((source, idx) => (
                                                        <Tag key={idx} color="blue" style={{ margin: 0 }}>{source}</Tag>
                                                    )) || <Text type="secondary">N/A</Text>}
                                                </Space>
                                            )
                                        }
                                    ]}
                                    pagination={false}
                                    style={{ background: '#ffffff' }}
                                    rowClassName={() => 'competency-table-row'}
                                />
                            </Card>
                        )}

                        {/* Deep Dive Analysis */}
                        {comprehensiveReport.deepDiveAnalysis && (
                            <Card
                                style={{
                                    marginBottom: '32px',
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                    border: '1px solid #e8e8e8'
                                }}
                                headStyle={{
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                    borderBottom: '2px solid #e8e8e8',
                                    borderRadius: '16px 16px 0 0',
                                    padding: '20px 24px'
                                }}
                                bodyStyle={{ padding: '24px' }}
                                title={
                                    <Flex align="center" gap={12}>
                                        <BulbOutlined style={{ fontSize: '20px', color: '#667eea' }} />
                                        <Title level={5} style={{ margin: 0, fontWeight: 700, color: '#1a1a1a' }}>
                                            Deep Dive Analysis
                                        </Title>
                                    </Flex>
                                }
                            >
                                <Space direction="vertical" style={{ width: '100%' }} size="large">
                                    {comprehensiveReport.deepDiveAnalysis.logicalReasoning && (
                                        <div style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <Flex align="center" gap={8} style={{ marginBottom: '12px' }}>
                                                <Text strong style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: 600 }}>
                                                    Logical Reasoning:
                                                </Text>
                                                <Badge
                                                    count={comprehensiveReport.deepDiveAnalysis.logicalReasoning.score}
                                                    style={{ backgroundColor: '#667eea' }}
                                                    overflowCount={10}
                                                />
                                                <Text style={{ color: '#8c8c8c' }}>/10</Text>
                                            </Flex>
                                            <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#595959', display: 'block' }}>
                                                {comprehensiveReport.deepDiveAnalysis.logicalReasoning.description}
                                            </Text>
                                        </div>
                                    )}
                                    {comprehensiveReport.deepDiveAnalysis.communicationStyle && (
                                        <div style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <Text strong style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
                                                Communication Style:
                                            </Text>
                                            <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#595959', display: 'block' }}>
                                                {comprehensiveReport.deepDiveAnalysis.communicationStyle.description}
                                            </Text>
                                        </div>
                                    )}
                                    {comprehensiveReport.deepDiveAnalysis.criticalThinking && (
                                        <div style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <Flex align="center" gap={8} style={{ marginBottom: '12px' }}>
                                                <Text strong style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: 600 }}>
                                                    Critical Thinking:
                                                </Text>
                                                <Badge
                                                    count={comprehensiveReport.deepDiveAnalysis.criticalThinking.score}
                                                    style={{ backgroundColor: '#667eea' }}
                                                    overflowCount={10}
                                                />
                                                <Text style={{ color: '#8c8c8c' }}>/10</Text>
                                            </Flex>
                                            <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#595959', display: 'block' }}>
                                                {comprehensiveReport.deepDiveAnalysis.criticalThinking.description}
                                            </Text>
                                        </div>
                                    )}
                                </Space>
                            </Card>
                        )}

                        <Flex gap={20} style={{ marginBottom: '32px' }}>
                            {/* AI Interview Section */}
                            {comprehensiveReport.aiInterview && (
                                <Card
                                    style={{
                                        flex: 1,
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        border: '1px solid #e8e8e8'
                                    }}
                                    headStyle={{
                                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                        borderBottom: '2px solid #e8e8e8',
                                        borderRadius: '16px 16px 0 0',
                                        padding: '20px 24px'
                                    }}
                                    bodyStyle={{ padding: '24px' }}
                                    title={
                                        <Flex align="center" gap={12}>
                                            <MessageOutlined style={{ fontSize: '20px', color: '#667eea' }} />
                                            <Title level={5} style={{ margin: 0, fontWeight: 700, color: '#1a1a1a' }}>
                                                AI Interview Evaluation
                                            </Title>
                                        </Flex>
                                    }
                                >
                                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                                        <Flex align="center" gap={12} wrap="wrap">
                                            <Flex align="baseline" gap={8}>
                                                <Text strong style={{ fontSize: '16px', color: '#595959', fontWeight: 600 }}>Score:</Text>
                                                <Text style={{ fontSize: '32px', fontWeight: 700, color: '#1a1a1a' }}>
                                                    {comprehensiveReport.aiInterview.score}/100
                                                </Text>
                                            </Flex>
                                            <Tag
                                                color={comprehensiveReport.aiInterview.score >= 70 ? 'green' :
                                                    comprehensiveReport.aiInterview.score >= 50 ? 'orange' : 'red'}
                                                style={{
                                                    fontSize: '14px',
                                                    padding: '6px 16px',
                                                    borderRadius: '20px',
                                                    fontWeight: 600,
                                                    border: 'none'
                                                }}
                                            >
                                                {comprehensiveReport.aiInterview.verdict}
                                            </Tag>
                                        </Flex>
                                        {comprehensiveReport.aiInterview.detailedAnalysis && (
                                            <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '12px',
                                                border: '1px solid #e8e8e8'
                                            }}>
                                                <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#595959', display: 'block' }}>
                                                    {comprehensiveReport.aiInterview.detailedAnalysis}
                                                </Text>
                                            </div>
                                        )}
                                    </Space>
                                </Card>
                            )}

                            {/* Coding Test Section */}
                            {comprehensiveReport.codingTest && (
                                <Card
                                    style={{
                                        flex: 1,
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        border: '1px solid #e8e8e8'
                                    }}
                                    headStyle={{
                                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                        borderBottom: '2px solid #e8e8e8',
                                        borderRadius: '16px 16px 0 0',
                                        padding: '20px 24px'
                                    }}
                                    bodyStyle={{ padding: '24px' }}
                                    title={
                                        <Flex align="center" gap={12}>
                                            <CodeOutlined style={{ fontSize: '20px', color: '#667eea' }} />
                                            <Title level={5} style={{ margin: 0, fontWeight: 700, color: '#1a1a1a' }}>
                                                Coding Test
                                            </Title>
                                        </Flex>
                                    }
                                >
                                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                                        <div style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <Flex align="baseline" gap={8} style={{ marginBottom: '12px' }}>
                                                <Text strong style={{ fontSize: '16px', color: '#595959', fontWeight: 600 }}>Average Score:</Text>
                                                <Text style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a' }}>
                                                    {comprehensiveReport.codingTest.score}/10
                                                </Text>
                                            </Flex>
                                            <Divider style={{ margin: '12px 0' }} />
                                            <Flex gap={24} wrap="wrap">
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                                                        Total Questions
                                                    </Text>
                                                    <Text strong style={{ fontSize: '18px', color: '#1a1a1a' }}>
                                                        {comprehensiveReport.codingTest.totalQuestions}
                                                    </Text>
                                                </div>
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                                                        Attempted
                                                    </Text>
                                                    <Text strong style={{ fontSize: '18px', color: '#1a1a1a' }}>
                                                        {comprehensiveReport.codingTest.attempted}
                                                    </Text>
                                                </div>
                                            </Flex>
                                        </div>
                                    </Space>
                                </Card>
                            )}
                        </Flex>
                    </Card>
                </div>
            )}

            {!isTestCompleted && !comprehensiveReport && (
                <Alert
                    message="Report Generation Available"
                    description="Comprehensive report can only be generated for completed tests."
                    type="info"
                    style={{ marginBottom: '16px' }}
                />
            )}

            {/* Tabs for Report Sections */}
            <Card style={{ marginBottom: '24px' }}>
                <Tabs
                    defaultActiveKey="overview"
                    type="card"
                    size="large"
                    items={[
                        {
                            key: 'overview',
                            label: (
                                <Space>
                                    <BarChartOutlined />
                                    <span>Overview</span>
                                </Space>
                            ),
                            children: (
                                <div>
                                    <div style={{ marginBottom: '24px' }}>
                                        <Table dataSource={dataSource} columns={columns} pagination={false} bordered />
                                    </div>
                                    <Title level={5} style={{ marginBottom: '16px' }}>
                                        MCQ's
                                    </Title>
                                    <MCQTabs mcqData={reportData?.mcq} />
                                    <Title level={5} style={{ marginBottom: '16px', marginTop: '24px' }}>
                                        Coding
                                    </Title>
                                    <CodingTabs codeData={reportData?.coding} />
                                    {reportData?.aiInterview && reportData.aiInterview.length > 0 && (
                                        <>
                                            <Title level={5} style={{ marginBottom: '16px', marginTop: '24px' }}>
                                                AI Interview
                                            </Title>
                                            <Collapse>
                                                {reportData.aiInterview.map((item: any, index: number) => (
                                                    <Panel
                                                        header={
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Text strong>Question {item.questionIndex + 1}: {item.question}</Text>
                                                                <Tag color={item.questionStatus === 'answered' ? 'green' : item.questionStatus === 'skipped' ? 'orange' : 'default'}>
                                                                    {item.questionStatus}
                                                                </Tag>
                                                            </div>
                                                        }
                                                        key={index}
                                                    >
                                                        <Card size="small" style={{ marginBottom: '16px' }}>
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <Text strong>Question: </Text>
                                                                <Text>{item.question}</Text>
                                                            </div>
                                                            {item.skills && item.skills.length > 0 && (
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <Text strong>Skills: </Text>
                                                                    {item.skills.map((skill: any, skillIndex: number) => (
                                                                        <Tag key={skillIndex} style={{ marginRight: '4px' }}>
                                                                            {skill.name || skill}
                                                                        </Tag>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {item.questionLevel && (
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <Text strong>Level: </Text>
                                                                    <Tag color={item.questionLevel === 'easy' ? 'green' : item.questionLevel === 'medium' ? 'orange' : 'red'}>
                                                                        {item.questionLevel}
                                                                    </Tag>
                                                                </div>
                                                            )}
                                                            {item.answer && (
                                                                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                                                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Answer:</Text>
                                                                    {item.answer.transcription && (
                                                                        <div style={{ marginBottom: '12px' }}>
                                                                            <Space>
                                                                                <FileTextOutlined />
                                                                                <Text strong>Transcript:</Text>
                                                                                <Button
                                                                                    type="link"
                                                                                    icon={<DownloadOutlined />}
                                                                                    onClick={() => {
                                                                                        const blob = new Blob([item.answer.transcription], { type: 'text/plain' });
                                                                                        const url = URL.createObjectURL(blob);
                                                                                        const a = document.createElement('a');
                                                                                        a.href = url;
                                                                                        a.download = `question-${item.questionIndex + 1}-transcript.txt`;
                                                                                        document.body.appendChild(a);
                                                                                        a.click();
                                                                                        document.body.removeChild(a);
                                                                                        URL.revokeObjectURL(url);
                                                                                    }}
                                                                                >
                                                                                    Download Transcript
                                                                                </Button>
                                                                            </Space>
                                                                            <div style={{ marginTop: '8px', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                                                                                <Text>{item.answer.transcription}</Text>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {item.answer.audioUrl && (() => {
                                                                        const fileUrl = process.env.FILE_URL || process.env.API_URL || '';
                                                                        const audioUrlPath = item.answer.audioUrl.startsWith('/')
                                                                            ? item.answer.audioUrl
                                                                            : `/${item.answer.audioUrl}`;
                                                                        const audioSrc = `${fileUrl}${audioUrlPath}`;

                                                                        return (
                                                                            <div>
                                                                                <div style={{ marginTop: '8px' }}>
                                                                                    <Text strong>Audio:</Text>
                                                                                    <audio
                                                                                        controls
                                                                                        style={{ width: '100%', marginTop: '8px' }}
                                                                                        preload="metadata"
                                                                                    >
                                                                                        <source src={audioSrc} type="audio/webm" />
                                                                                        <source src={audioSrc} type="audio/mpeg" />
                                                                                        <source src={audioSrc} type="audio/wav" />
                                                                                        <source src={audioSrc} type="audio/ogg" />
                                                                                        Your browser does not support the audio element.
                                                                                    </audio>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                    {item.answer.score !== undefined && item.answer.score !== null && (
                                                                        <div style={{ marginTop: '12px' }}>
                                                                            <Text strong>Score: </Text>
                                                                            <Tag color={item.answer.score >= 7 ? 'green' : item.answer.score >= 5 ? 'orange' : 'red'}>
                                                                                {item.answer.score}/10
                                                                            </Tag>
                                                                        </div>
                                                                    )}
                                                                    {item.answer.evaluationFeedback && (
                                                                        <div style={{ marginTop: '12px' }}>
                                                                            <Text strong>Evaluation Feedback: </Text>
                                                                            <div style={{ marginTop: '4px', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                                                                                <Text>{item.answer.evaluationFeedback}</Text>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {!item.answer && (
                                                                <Text type="secondary">No answer provided</Text>
                                                            )}
                                                        </Card>
                                                    </Panel>
                                                ))}
                                            </Collapse>
                                        </>
                                    )}
                                    {/* Consent Recording Section */}
                                    {(reportData.consentRecordingUrl || reportData.consentTranscript) && (
                                        <>
                                            <Title level={5} style={{ marginBottom: '16px', marginTop: '24px' }}>
                                                Consent Recording
                                            </Title>
                                            <Card size="small" style={{ marginBottom: '16px' }}>
                                                {reportData.consentTranscript && (
                                                    <div style={{ marginBottom: '16px' }}>
                                                        <Space>
                                                            <FileTextOutlined />
                                                            <Text strong>Transcript:</Text>
                                                            <Button
                                                                type="link"
                                                                icon={<DownloadOutlined />}
                                                                onClick={() => {
                                                                    const blob = new Blob([reportData.consentTranscript], { type: 'text/plain' });
                                                                    const url = URL.createObjectURL(blob);
                                                                    const a = document.createElement('a');
                                                                    a.href = url;
                                                                    a.download = 'consent-transcript.txt';
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    document.body.removeChild(a);
                                                                    URL.revokeObjectURL(url);
                                                                }}
                                                            >
                                                                Download Transcript
                                                            </Button>
                                                        </Space>
                                                        <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                                            <Text>{reportData.consentTranscript}</Text>
                                                        </div>
                                                    </div>
                                                )}
                                                {reportData.consentRecordingUrl && (() => {
                                                    const fileUrl = process.env.FILE_URL || process.env.API_URL || '';
                                                    const audioUrlPath = reportData.consentRecordingUrl.startsWith('/')
                                                        ? reportData.consentRecordingUrl
                                                        : `/${reportData.consentRecordingUrl}`;
                                                    const audioSrc = `${fileUrl}${audioUrlPath}`;

                                                    return (
                                                        <div>
                                                            <Space>
                                                                <AudioOutlined />
                                                                <Text strong>Audio:</Text>
                                                            </Space>
                                                            <div style={{ marginTop: '8px' }}>
                                                                <audio
                                                                    controls
                                                                    style={{ width: '100%' }}
                                                                    preload="metadata"
                                                                >
                                                                    <source src={audioSrc} type="audio/webm" />
                                                                    <source src={audioSrc.replace('.webm', '.mp3')} type="audio/mpeg" />
                                                                    <source src={audioSrc.replace('.webm', '.wav')} type="audio/wav" />
                                                                    <source src={audioSrc.replace('.webm', '.ogg')} type="audio/ogg" />
                                                                    Your browser does not support the audio element.
                                                                </audio>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </Card>
                                        </>
                                    )}

                                    {/* Introduction Recording Section */}
                                    {(reportData.introductionRecordingUrl || reportData.introductionTranscript) && (
                                        <>
                                            <Title level={5} style={{ marginBottom: '16px', marginTop: '24px' }}>
                                                Introduction Recording
                                            </Title>
                                            <Card size="small" style={{ marginBottom: '16px' }}>
                                                {reportData.introductionTranscript && (
                                                    <div style={{ marginBottom: '16px' }}>
                                                        <Space>
                                                            <FileTextOutlined />
                                                            <Text strong>Transcript:</Text>
                                                            <Button
                                                                type="link"
                                                                icon={<DownloadOutlined />}
                                                                onClick={() => {
                                                                    const blob = new Blob([reportData.introductionTranscript], { type: 'text/plain' });
                                                                    const url = URL.createObjectURL(blob);
                                                                    const a = document.createElement('a');
                                                                    a.href = url;
                                                                    a.download = 'introduction-transcript.txt';
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    document.body.removeChild(a);
                                                                    URL.revokeObjectURL(url);
                                                                }}
                                                            >
                                                                Download Transcript
                                                            </Button>
                                                        </Space>
                                                        <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                                            <Text>{reportData.introductionTranscript}</Text>
                                                        </div>
                                                    </div>
                                                )}
                                                {reportData.introductionRecordingUrl && (() => {
                                                    const fileUrl = process.env.FILE_URL || process.env.API_URL || '';
                                                    const audioUrlPath = reportData.introductionRecordingUrl.startsWith('/')
                                                        ? reportData.introductionRecordingUrl
                                                        : `/${reportData.introductionRecordingUrl}`;
                                                    const audioSrc = `${fileUrl}${audioUrlPath}`;

                                                    return (
                                                        <div>
                                                            <Space>
                                                                <AudioOutlined />
                                                                <Text strong>Audio:</Text>
                                                            </Space>
                                                            <div style={{ marginTop: '8px' }}>
                                                                <audio
                                                                    controls
                                                                    style={{ width: '100%' }}
                                                                    preload="metadata"
                                                                >
                                                                    <source src={audioSrc} type="audio/webm" />
                                                                    <source src={audioSrc.replace('.webm', '.mp3')} type="audio/mpeg" />
                                                                    <source src={audioSrc.replace('.webm', '.wav')} type="audio/wav" />
                                                                    <source src={audioSrc.replace('.webm', '.ogg')} type="audio/ogg" />
                                                                    Your browser does not support the audio element.
                                                                </audio>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </Card>
                                        </>
                                    )}

                                    {reportData.photos && reportData.photos.length > 0 && (
                                        <>
                                            <Title level={5} style={{ marginBottom: '16px' }}>
                                                Photos
                                            </Title>
                                            <Image.PreviewGroup>
                                                <Row gutter={[16, 16]}>
                                                    {reportData.photos.map((src, index) => (
                                                        <Col key={index} xs={24} sm={12} md={6} lg={4}>
                                                            <Image
                                                                src={`${process.env.FILE_URL}${src}`}
                                                                alt={`Gallery image ${index + 1}`}
                                                                style={{ width: '100%', borderRadius: '10px' }}
                                                            />
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Image.PreviewGroup>
                                        </>
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: 'logs',
                            label: (
                                <Space>
                                    <UnorderedListOutlined />
                                    <span>Test Logs</span>
                                </Space>
                            ),
                            children: id ? <TestLogsTab testId={id} /> : <Empty description="No test ID available" />,
                        },
                    ]}
                />
            </Card>
        </div>
    )
}

export default Report